import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { eventType } from "@/lib/constants";
import CinemaBrand from "@/components/CinemaBrand";
import { DisplayMusicByToken } from "@/components/DisplayMusic";
import { Cloud, Star } from "lucide-react";

/**
 * Movie House TV — dauerhafte Eingangsbereich-Anzeige.
 * Rotiert: Trailer / Poster / Countdown / Wetter / Programm / Bewertungen.
 */
export default function MovieHouseTV() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [rotor, setRotor] = useState(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const load = () => api.get(`/displays/public/${token}`).then((r) => setData(r.data.tv)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    const w = () => api.get(`/weather`).then((r) => setWeather(r.data)).catch(() => {});
    w();
    const wi = setInterval(w, 15 * 60000);
    const c = setInterval(() => setNow(new Date()), 1000);
    const rot = setInterval(() => setRotor((r) => (r + 1) % 4), 15000);
    return () => { clearInterval(t); clearInterval(wi); clearInterval(c); clearInterval(rot); };
  }, [token]);

  const next = data?.next;
  const nextMovie = next?.movie;

  const countdown = useMemo(() => {
    if (!next) return null;
    const target = new Date(`${next.date}T${next.start_time}:00`);
    let d = target - now;
    if (d < 0) return "läuft";
    const h = Math.floor(d / 3600000); d %= 3600000;
    const m = Math.floor(d / 60000); d %= 60000;
    const s = Math.floor(d / 1000);
    return `${h > 0 ? h + "h " : ""}${m}m ${s.toString().padStart(2, "0")}s`;
  }, [next, now]);

  if (!data) return <div className="min-h-screen bg-black flex items-center justify-center text-[#A8A398]">Lade Movie House TV …</div>;

  return (
    <div className="min-h-screen w-full bg-black text-[#F5F0E6] relative overflow-hidden">
      <DisplayMusicByToken token={token} autoStart hideToggle />
      {(next?.wallpaper_url || nextMovie?.backdrop_url) && (
        <>
          <img src={next?.wallpaper_url || nextMovie?.backdrop_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover opacity-30 blur-sm" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
        </>
      )}
      <div className="relative z-10 min-h-screen grid grid-rows-[auto_1fr_auto] p-8 md:p-12">
        {/* Top bar */}
        <header className="flex items-center justify-between">
          <CinemaBrand size="xl" stacked={false} className="items-center" />
          <div className="text-right">
            <div className="font-mono text-3xl md:text-5xl tabular-nums">{now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</div>
            <div className="text-xs text-[#A8A398] mt-1">{now.toLocaleDateString("de-DE", { weekday: "long" }) + ", " + now.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }).replaceAll(".", "/")}</div>
          </div>
        </header>

        {/* Rotating hero */}
        <main className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center py-8">
          <div>
            {rotor === 0 && <HeroNextShow next={next} nextMovie={nextMovie} countdown={countdown} />}
            {rotor === 1 && <HeroTrailer movie={nextMovie} />}
            {rotor === 2 && <HeroRated movies={data.top_rated} />}
            {rotor === 3 && <HeroPosters movies={data.movies} />}
          </div>

          {/* Right column: Weather + Program */}
          <aside className="space-y-6">
            <WeatherCard weather={weather} />
            <ProgramCard upcoming={data.upcoming} />
          </aside>
        </main>

        <footer className="flex items-center justify-between text-[10px] uppercase tracking-[0.32em] text-[#575148]">
          <div>Movie House TV · Live</div>
          <div className="flex gap-2">{[0,1,2,3].map((i) => <span key={i} className={`inline-block w-2 h-2 rounded-full ${i === rotor ? "bg-[#D4AF37]" : "bg-[#2A2A2A]"}`} />)}</div>
          <div>tmdb.org (nicht offiziell)</div>
        </footer>
      </div>
    </div>
  );
}

function HeroNextShow({ next, nextMovie, countdown }) {
  if (!next || !nextMovie) return <div className="text-2xl font-heading text-[#A8A398]">Zurzeit keine Vorstellung geplant.</div>;
  const ev = eventType(next.event_type);
  return (
    <div className="flex gap-8 items-center">
      {nextMovie.poster_url && <img src={nextMovie.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-48 md:w-56 aspect-[2/3] object-cover rounded shadow-2xl" />}
      <div className="min-w-0">
        {ev.v !== "regular" && (
          <div className="text-xs uppercase tracking-[0.4em] mb-2" style={{ color: ev.color }}>{ev.icon} {ev.l}</div>
        )}
        <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37]">Als Nächstes</div>
        <div className="font-heading text-5xl md:text-7xl mt-2 leading-none">{nextMovie.title_de}</div>
        <div className="text-[#A8A398] mt-2">{nextMovie.genre} · {nextMovie.duration_min} Min</div>
        <div className="mt-5 flex gap-6">
          <TinyStat label="Einlass" v={next.entry_time} />
          <TinyStat label="Start" v={next.start_time} />
          <TinyStat label="Saal" v={next.hall} />
        </div>
        <div className="mt-6">
          <div className="text-xs uppercase tracking-[0.32em] text-[#A8A398]">Countdown</div>
          <div className="font-mono text-4xl md:text-6xl text-[#D4AF37] tabular-nums">{countdown}</div>
        </div>
      </div>
    </div>
  );
}

function HeroTrailer({ movie }) {
  if (!movie) return <div className="text-2xl text-[#A8A398]">Kein Trailer verfügbar.</div>;
  const ytId = getYoutubeId(movie.trailer_url);
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Trailer</div>
      <div className="font-heading text-4xl md:text-5xl mb-4">{movie.title_de}</div>
      <div className="aspect-video bg-black rounded overflow-hidden border border-[#2A2A2A]">
        {ytId ? (
          <iframe title="trailer" className="w-full h-full" src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${ytId}&modestbranding=1`} allow="autoplay; encrypted-media" />
        ) : (
          movie.backdrop_url ? <img src={movie.backdrop_url} alt="" className="w-full h-full object-cover" /> : null
        )}
      </div>
    </div>
  );
}

function HeroRated({ movies }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Top Bewertungen</div>
      <div className="grid grid-cols-3 gap-4">
        {(movies || []).slice(0, 6).map((m) => (
          <div key={m.id} className="card-lux overflow-hidden">
            <div className="aspect-[2/3] bg-[#1A1A1A]">
              {m.poster_url && <img src={m.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />}
            </div>
            <div className="p-3">
              <div className="font-heading text-lg truncate">{m.title_de}</div>
              <div className="flex items-center gap-1 text-[#D4AF37] text-sm mt-1"><Star size={14} fill="currentColor" /> {m.personal_rating?.toFixed(1)}</div>
            </div>
          </div>
        ))}
        {!movies?.length && <div className="text-[#A8A398] col-span-full">Noch keine Bewertungen.</div>}
      </div>
    </div>
  );
}

function HeroPosters({ movies }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Aus dem Programm</div>
      <div className="grid grid-cols-4 gap-3">
        {(movies || []).slice(0, 8).map((m) => (
          <div key={m.id} className="aspect-[2/3] bg-[#1A1A1A] rounded overflow-hidden">
            {m.poster_url && <img src={m.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function WeatherCard({ weather }) {
  const c = weather?.current;
  const code = c?.weather_code;
  const desc = weatherDesc(code);
  return (
    <div className="card-lux p-5">
      <div className="text-xs uppercase tracking-[0.32em] text-[#A8A398] flex items-center gap-2"><Cloud size={14} /> Wetter</div>
      {c ? (
        <>
          <div className="font-heading text-5xl mt-2">{Math.round(c.temperature_2m)}°C</div>
          <div className="text-sm text-[#A8A398] mt-1">{desc}</div>
          <div className="text-xs text-[#A8A398] mt-2 font-mono">Wind {Math.round(c.wind_speed_10m)} km/h</div>
        </>
      ) : (
        <div className="text-sm text-[#A8A398] mt-2">Lade …</div>
      )}
    </div>
  );
}
function ProgramCard({ upcoming }) {
  return (
    <div className="card-lux p-5">
      <div className="text-xs uppercase tracking-[0.32em] text-[#A8A398]">Heute & Nächste</div>
      <div className="mt-3 space-y-2 max-h-72 overflow-hidden">
        {(upcoming || []).slice(0, 6).map((s) => (
          <div key={s.id} className="flex items-baseline gap-3 border-b border-[#1A1A1A] py-1.5">
            <div className="font-mono text-[#D4AF37] text-sm w-20">{fmtShortDate(s.date)}</div>
            <div className="font-mono text-sm w-14">{s.start_time}</div>
            <div className="truncate">{s.movie?.title_de || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TinyStat({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</div>
      <div className="font-mono text-2xl">{v}</div>
    </div>
  );
}

function getYoutubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    return u.searchParams.get("v");
  } catch { return null; }
}

function fmtShortDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  } catch { return iso; }
}

function weatherDesc(code) {  const M = {
    0: "Klar", 1: "Meist klar", 2: "Teils bewölkt", 3: "Bedeckt",
    45: "Nebel", 48: "Reifnebel",
    51: "Leichter Sprühregen", 53: "Sprühregen", 55: "Starker Sprühregen",
    61: "Leichter Regen", 63: "Regen", 65: "Starker Regen",
    71: "Leichter Schnee", 73: "Schnee", 75: "Starker Schnee",
    80: "Regenschauer", 81: "Starke Schauer", 82: "Heftige Schauer",
    95: "Gewitter", 96: "Gewitter mit Hagel", 99: "Schweres Gewitter",
  };
  return M[code] || "—";
}
